import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import {
  handleAccept,
  handleClose,
  handleEdit,
  handlePause,
  handleReactivate,
  handleReopen,
} from "../../../utils/Rma/rmaHandlers";
import { RmaData } from "../../../utils/Rma/rmaTypes";
import "../Modal.css";
import ConfirmCloseModal from "./ConfirmClose";
import ForwardModal from "./ForwardModal";
import JournalModal from "./JournalModal";
import CommunicationModal from "./CommunicationModal";
import { authFetch } from "../../../utils/Security/authFetch";

const isAdmin = localStorage.getItem("isAdmin") === "true";

type Message = {
  id: number;
  username: string;
  message: string;
  date: string;
};
type Communication = {
  id: number;
  users: string;
  message: string;
  date: string;
};

type ModalProps = {
  isOpen: boolean;
  data: any;
  onClose: () => void;
};

const hiddenKeys = [
  "isaccepted",
  "isFavorite",
  "isallsite",
  "isclosed",
  "foruserid",
  "isforwarded",
  "isreopened",
  "isreactivated",
];

// Sections and fieldLabels (unchanged)
const ticketInfo = [
  "createdby",
  "userid",
  "ticketId",
  "status",
  "ticketCreationDatetime",
  "ticketClosureDatetime",
  "vendorrmacross",
  "trackstage",
];
const customerInfo = [
  "affectedCustomer",
  "affectedNetwork",
  "affectedSite",
  "customername",
  "pointofcontact",
];
const device = [
  "brand",
  "model",
  "partnumber",
  "serialnumber",
  "password",
  "activewarranty",
  "faultdescription",
  "solution",
  "companyname",
];
const info = [
  "ior",
  "city",
  "name",
  "region",
  "email",
  "country",
  "phonenumber",
  "zip",
  "address1",
  "address2",
];
const slaInfo = [
  "sla",
  "slaStatus",
  "ola",
  "olaStatus",
  "reactionPoint",
  "responseTimeToOpen",
  "responseTimeToClose",
];
const statusInfo = [
  "summary",
  "description",

  "closureCode",
  "errorType",
  "filePath",
];
const resInfo = ["responsiblePerson", "responsibleTeam"];
const miscInfo = ["journalUpdate", "changetemplate", "attachments", "filePath"];
const incInfo = ["impact", "priority", "urgency"];
const relInfo = ["relatedobject", "filePath"];

const fieldLabels: { [key: string]: string } = {
  // Ticket Info
  createdby: "Created By",
  user: "User",
  userid: "User",
  ticketId: "Ticket ID",
  status: "Status",
  ior: "Is the customer the Importer of Record (IoR)?",
  ticketCreationDatetime: "Ticket Creation Date/Time",
  ticketClosureDatetime: "Ticket Closure Date/Time",
  rmaStartDatetime: "RMA Start Date/Time",
  rmaCloseDatetime: "RMA Close Date/Time",
  customerComplaint: "Customer Complaint",
  customerInformed: "Customer Informed",
  vendorrmacross: "Vendor RMA Cross Reference Number",
  trackStage: "Track Stage",
  companyname: "Company Name",
  pointofcontact: "Point Of Contact",
  // Customer Info
  affectedCustomer: "Customer",
  affectedSite: "Site",
  affectedNetwork: "Network",
  affectedLink: "Link",
  asset: "Asset",
  // SLA Info
  sla: "SLA",
  slaStatus: "SLA Status",
  ola: "OLA",
  olaStatus: "OLA Status",
  reactionPoint: "Reaction Point",
  responseTimeToOpen: "Response Time to Open",
  responseTimeToClose: "Response Time to Close",
  tracklocation: "Track Stage",
  // Status Info
  summary: "Summary",
  description: "Description",
  solution: "Solution",
  closureCode: "Closure Code",
  errorType: "Error Type",
  filePath: "File Path",

  // Responsible
  responsiblePerson: "Responsible Person",
  responsibleTeam: "Responsible Team",

  // Miscellaneous
  journalUpdate: "Journal Update",
  changetemplate: "Change Template",
  attachments: "Attachments",
  activewarranty: "Active Warranty",

  // Incident Info
  impact: "Impact",
  priority: "Priority",
  urgency: "Urgency",
  customername: "Customer Name",
  // Related
  relatedobject: "Related Object",
  brand: "Brand",
  model: "Model",
  partnumber: "Part Number",
  serialnumber: "Serial Number",
  password: "Password",
  faultdescription: "Fault Description/Analysis/Troubleshooting",
  city: "City",
  name: "Name",
  region: "Region",
  email: "Email",
  country: "Country",
  phonenumber: "Phone Number",
  zip: "Zip",
  address1: "Address1",
  address2: "Address2",
};
const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  return dateStr.replace("T", " ").split(".")[0];
};
const renderAttachment = (filePathArray: string[] | undefined) => {
  if (!filePathArray || filePathArray.length === 0) return null;

  const filePath = filePathArray[0]; // get first item from array

  if (typeof filePath !== "string" || filePath.trim() === "") return null;

  const extension = filePath.split(".").pop()?.toLowerCase();
  const url = `http://149.202.42.77:8080/${filePath.replace(/\\/g, "/")}`;

  if (!extension) return null;

  if (["png", "jpg", "jpeg", "gif", "bmp"].includes(extension)) {
    return (
      <img
        src={url}
        alt="attachment"
        style={{ maxWidth: "100%", maxHeight: 300 }}
      />
    );
  }

  return null;
};

const Modal: React.FC<ModalProps> = ({ isOpen, data, onClose }) => {
  const navigate = useNavigate();
  const [selectedRma, setSelectedRma] = useState<RmaData | null>(null);
  const [journalMessages, setJournalMessages] = useState<Message[]>([]);
  const [showClose, setShowClose] = useState(false);

  const [communicationMessages, setCommunicationMessages] = useState<
    Communication[]
  >([]);
  const [fileImages, setFileImages] = useState<{
    [path: string]: string | null;
  }>({});
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [showCommunication, setShowCommunication] = useState(false);
  const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
  const [journalTitle, setJournalTitle] = useState("Add Journal");
  const [pauseAfterJournal, setPauseAfterJournal] = useState(false); // 🟢 new state
  const [IsReturn, setIsReturn] = useState(false); // 🟢 new state

  const [username, setUsername] = useState<string>("");
  // Reset username on modal close
  useEffect(() => {
    if (!isOpen) {
      setIsReturn(false);
      setUsername("");
      return;
    }

    // When modal is open, handle trackstage properly
    if (data?.trackstage === "Returned") {
      setIsReturn(true);
    } else {
      setIsReturn(false);
    }
  }, [isOpen, data?.trackstage]);
  console.log("TRACKSTAGE =", data?.trackstage);
  console.log("IsReturn =", IsReturn);
  useEffect(() => {
    if (isOpen && data?.ticketId) {
      fetchMessages();
      fetchUsername();
      fetchCommunication();
    }
  }, [isOpen, data]);

  const fetchUsername = async () => {
    if (!data?.userid) return;
    //console.log("fetchUsername called with userid:", data.userid);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/users/${data.userid}`
      );
      if (!res.ok) throw new Error("User not found");
      const user = await res.json();
      //console.log("Fetched user:", user);
      setUsername(user.username || `User #${data.userid}`);
    } catch (err) {
      //console.log("Error fetching username:", err);
      setUsername(`User #${data.userid}`);
    }
  };

  const fetchMessages = async () => {
    if (!data?.ticketId) return;
    const res = await authFetch(
      `${config.apiBaseUrl}/api/messages/ticket/${data.ticketId}`
    );
    if (res.ok) setJournalMessages(await res.json());
  };

  const fetchCommunication = async () => {
    if (!data?.ticketId) return;
    const res = await authFetch(
      `${config.apiBaseUrl}/api/communications/ticket/${data.ticketId}`
    );
    if (res.ok) setCommunicationMessages(await res.json());
  };

  if (!isOpen) return null;

  const isAccepted = data?.isaccepted;
  const isClosed = data?.isclosed;
  const isPaused = data?.ispaused;

  const handleForward = (data: RmaData) => {
    setSelectedRma(data);
    setIsForwardModalOpen(true);
  };
  const fetchUpdatedRma = async (): Promise<RmaData> => {
    const res = await authFetch(
      `${config.apiBaseUrl}/api/rmas/${data.ticketId}`
    );
    console.log("res.json>>>", res.json);
    return await res.json();
  };
  const confirmClose = async () => {
    setShowConfirmClose(false);
    onClose();
    await new Promise((resolve) => setTimeout(resolve, 4000));
    const updatedData = await fetchUpdatedRma();
    await handleClose(updatedData, fetchMessages);
  };
  const closerma = async () => {
    onClose();
    await new Promise((resolve) => setTimeout(resolve, 4000));
    const updatedData = await fetchUpdatedRma();
    await handleClose(updatedData, fetchMessages);
  };

  const addjournal = () => {
    handlePause(data, fetchMessages);
    setShowJournal(false);
    onClose();
  };
  const handleJournalConfirm = async () => {
    if (pauseAfterJournal) {
      await handlePause(data, fetchMessages);
    }
    setPauseAfterJournal(false);
    setShowJournal(false);
    onClose();
  };
  const handlePauseWithJournal = () => {
    setPauseAfterJournal(true);
    setJournalTitle("Pause Rma (Add Journal)");
    setShowJournal(true);
  };
  const addcommunication = () => {
    setShowCommunication(false);
    onClose();
  };
  const downloadFile = async (path: string) => {
    try {
      if (!path) return;
      const normalizedPath = path.replace(/\\/g, "/");
      const originalFileName = normalizedPath.split("/").pop();
      if (!originalFileName) return;

      const res = await authFetch(
        `${
          config.apiBaseUrl
        }/api/changes/downloadFile?path=${encodeURIComponent(path)}`
      );
      if (!res.ok) throw new Error("Failed to download file");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = originalFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    }
  };
  const renderSection = (title: string, keys: string[], data: any) => (
    <div className="modal-section">
      <h3>{title}</h3>
      <div className="modal-section-content">
        {keys
          .filter((key) => {
            if (
              !isAdmin &&
              [
                "reactionPoint",
                "responseTimeToOpen",
                "responseTimeToClose",
                "impact",
                "priority",
                "acceptanceTime",
                "sla",
                "slaStatus",
                "ola",
                "olaStatus",
                "vendorrmacross",
                "rmaCrossReference",
                "relatedobject",
                "customerComplaint",
                "customerInformed",
              ].includes(key)
            ) {
              return false;
            }
            return data[key] !== undefined && !hiddenKeys.includes(key);
          })
          .map((key, index) => (
            <div key={index} className="modal-item">
              <strong>{fieldLabels[key] || key}:</strong>
              <div className="modal-value">
                {key === "filePath" ? (
                  /* ================= FILE PATH (RMA) ================= */
                  Array.isArray(data[key]) ? (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {data[key].map((path: string | null, i: number) => {
                        if (!path) return null;

                        const normalizedPath = path.replace(/\\/g, "/");
                        const fileName =
                          normalizedPath.split("/").pop() || `File${i + 1}`;
                        const ext = fileName.split(".").pop()?.toLowerCase();
                        const isImage = [
                          "png",
                          "jpg",
                          "jpeg",
                          "gif",
                          "bmp",
                          "webp",
                        ].includes(ext || "");
                        const imgUrl = fileImages[path] || null;

                        return (
                          <li key={i} style={{ marginBottom: "10px" }}>
                            {isImage && imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={fileName}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "300px",
                                  borderRadius: "5px",
                                }}
                              />
                            ) : (
                              <button
                                style={{
                                  color: "blue",
                                  textDecoration: "underline",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                                onClick={() => downloadFile(path)}
                              >
                                {fileName}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    (() => {
                      const normalizedPath =
                        data[key]?.replace(/\\/g, "/") || "";
                      const fileName =
                        normalizedPath.split("/").pop() || "file";
                      const ext = fileName.split(".").pop()?.toLowerCase();
                      const isImage = [
                        "png",
                        "jpg",
                        "jpeg",
                        "gif",
                        "bmp",
                        "webp",
                      ].includes(ext || "");
                      const fileUrl = `${config.apiBaseUrl}/api/rmas/files/${fileName}`;

                      return isImage ? (
                        <img
                          src={fileUrl}
                          alt={fileName}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "5px",
                          }}
                        />
                      ) : (
                        <button
                          style={{
                            color: "blue",
                            textDecoration: "underline",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                          }}
                          onClick={() => downloadFile(data[key])}
                        >
                          {fileName}
                        </button>
                      );
                    })()
                  )
                ) : key === "relatedobject" && data[key] ? (
                  /* ================= ✅ RELATED OBJECT ================= */
                  (Array.isArray(data[key])
                    ? data[key]
                    : (data[key] as string).split(",")
                  ).map((item: string, idx: number) => {
                    const ticketId = item.trim();
                    let path = `/incident-edit/${ticketId}`;

                    if (ticketId.startsWith("TSK"))
                      path = `/task-edit/${ticketId}`;
                    else if (ticketId.startsWith("CHN"))
                      path = `/change-edit/${ticketId}`;
                    else if (ticketId.startsWith("IND"))
                      path = `/incident-edit/${ticketId}`;

                    const total = Array.isArray(data[key])
                      ? data[key].length
                      : (data[key] as string).split(",").length;

                    return (
                      <React.Fragment key={idx}>
                        <button
                          style={{
                            color: "blue",
                            textDecoration: "underline",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            marginRight: 5,
                          }}
                          onClick={() => navigate(path, { state: { data } })}
                        >
                          {ticketId}
                        </button>
                        {idx < total - 1 ? ", " : ""}
                      </React.Fragment>
                    );
                  })
                ) : (
                  /* ================= DEFAULT VALUE ================= */
                  (() => {
                    const value = data[key];

                    if (
                      value === "" ||
                      JSON.stringify(value) === "[]" ||
                      JSON.stringify(value) === "{}"
                    )
                      return "";

                    if (
                      key === "description" ||
                      key === "faultdescription" ||
                      key === "summary" ||
                      key === "risk" ||
                      key === "rollbackstrategy" ||
                      key === "requiredresources" ||
                      key === "solution"
                    ) {
                      return (
                        <div
                          dangerouslySetInnerHTML={{ __html: value }}
                          style={{ whiteSpace: "normal" }}
                        />
                      );
                    }

                    if (Array.isArray(value)) return value.join(", ");

                    // ✅ USER ID OVERRIDE
                    if (key === "userid") {
                      return username;
                    }

                    if (typeof value === "string") {
                      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
                        return formatDate(value);
                      }
                      return value;
                    }

                    return JSON.stringify(value, null, 2);
                  })()
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <>
      {isForwardModalOpen && (
        <ForwardModal
          isOpen={isForwardModalOpen}
          data={selectedRma}
          onClose={() => {
            setIsForwardModalOpen(false);
            setSelectedRma(null);
          }}
        />
      )}

      {showConfirmClose && (
        <ConfirmCloseModal
          data={data}
          onConfirm={confirmClose}
          onCancel={() => setShowConfirmClose(false)}
        />
      )}

      {showJournal && (
        <JournalModal
          title={journalTitle}
          data={data}
          onConfirm={handleJournalConfirm}
          onCancel={() => {
            setPauseAfterJournal(false);
            setShowJournal(false);
          }}
        />
      )}

      {showCommunication && (
        <CommunicationModal
          data={data}
          onConfirm={addcommunication}
          onCancel={() => setShowCommunication(false)}
        />
      )}

      <div className="modal-overlay">
        <div className="modal-content">
          <div
            className="modal-top-buttons"
            style={{
              position: "sticky",
              top: -32,
              backgroundColor: "#fff",
              zIndex: 100,
              padding: "10px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {!isClosed && isAdmin && (
              <button onClick={() => navigate(`/rma-edit/${data.ticketId}`)}>
                Edit
              </button>
            )}
            {!isAccepted && isAdmin && (
              <button onClick={() => handleAccept(data, fetchMessages)}>
                Accept
              </button>
            )}
            {isAccepted && !isPaused && !isClosed && isAdmin && (
              <>
                <button disabled>Already Accepted</button>
                <button onClick={handlePauseWithJournal}>Pause</button>
                {IsReturn && <button onClick={() => closerma()}>Close</button>}
              </>
            )}
            {isPaused && isAdmin && (
              <>
                <button onClick={() => handleReactivate(data, fetchMessages)}>
                  Reactivate
                </button>
                {IsReturn && (
                  <button onClick={() => setShowConfirmClose(true)}>
                    Close
                  </button>
                )}
              </>
            )}
            {isClosed && isAdmin && (
              <button onClick={() => handleReopen(data, fetchMessages)}>
                Reopen
              </button>
            )}
            {isAdmin && !isClosed && (
              <button onClick={() => handleForward(data)}>Forward</button>
            )}
            <button onClick={onClose}>X</button>
          </div>

          <h2
            style={{
              position: "sticky",
              top: 16,
              backgroundColor: "#fff",
              zIndex: 99,
              margin: 0,
              padding: "10px 0",
            }}
          >
            RMA Details
          </h2>
          <div className="modal-body">
            {renderSection("RMA Information", ticketInfo, data)}
            {renderSection("Customer Contact Information", customerInfo, data)}
            {renderSection("Device Information", device, data)}
            {renderSection(" Information", info, data)}
            {isAdmin && renderSection("Related Object", relInfo, data)}
          </div>

          <div className="messages-container">
            {isAdmin && (
              <div className="journal">
                <button onClick={() => setShowJournal(true)}>
                  Add Journal
                </button>
                <h3>Journal</h3>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {[...journalMessages]
                    .sort(
                      (a, b) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    )
                    .map((msg) => (
                      <li
                        key={msg.id}
                        style={{
                          marginBottom: "1rem",
                          wordBreak: "break-word",
                        }}
                      >
                        <strong>{msg.username}</strong>:&nbsp;
                        <span
                          dangerouslySetInnerHTML={{ __html: msg.message }}
                          style={{
                            display: "inline-block",
                            maxWidth: "100%",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                          }}
                        />
                        <br />
                        <small>{new Date(msg.date).toLocaleString()}</small>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            <div className="journal">
              <button onClick={() => setShowCommunication(true)}>
                Add Communication
              </button>

              <h3>Communication</h3>
              <ul>
                {[...communicationMessages]
                  .sort(
                    (a, b) =>
                      new Date(b.date).getTime() - new Date(a.date).getTime()
                  )
                  .map((msg) => (
                    <li key={msg.id}>
                      <strong>{msg.users}</strong>: {msg.message}
                      <br />
                      <small>{new Date(msg.date).toLocaleString()}</small>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Modal;
